import React from "react";
import {
  ShareOverlay,
  ShareContainer,
  ShareContent,
  ShareHeader,
  ShareTitle,
  CloseButton,
  SearchInput,
  ChipsWrap,
  PillChip,
  SelectAllRow,
  ListContainer,
  UserRow,
  Footer,
  ClearButton,
  ShareButton,
  LoadingWrap,
  EmptyWrap,
  EmptyChipsHint,
} from "./ShareModal.styled";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import CloseIcon from "@mui/icons-material/Close";
import Checkbox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { useUserApi } from "../../hooks/useUserApi";
import { useCollaboration } from "../../hooks/useCollaboration";
import { StorageHelper } from "../../utils/storageHelper"; // ⬅️ fallback to login data

/* ---------------- utils ---------------- */
const norm = (s) => (s ?? "").trim().toLowerCase();
const toId = (v) => (v == null ? undefined : String(v));

const isValidUser = (u) =>
  !!u && typeof u.user_id === "string" && u.user_id.trim().length > 0;

function looksLikeUserArray(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return false;
  return arr.some(
    (u) =>
      u &&
      typeof u === "object" &&
      ("email" in u || Object.keys(u || {}).some((k) => /email/i.test(k)))
  );
}
function findFirstArray(input) {
  if (!input) return [];
  if (Array.isArray(input)) return input;
  const queue = [input];
  const seen = new Set();
  while (queue.length) {
    const node = queue.shift();
    if (!node || typeof node !== "object") continue;
    if (seen.has(node)) continue;
    seen.add(node);
    for (const key of Object.keys(node)) {
      const val = node[key];
      if (Array.isArray(val)) return val;
      if (val && typeof val === "object") queue.push(val);
    }
  }
  return [];
}
function extractUsersArrayFlexible(resp) {
  const candidates = [
    resp?.data?.GetUsers,
    resp?.data?.data?.GetUsers,
    resp?.GetUsers,
    resp?.data,
    resp,
  ];
  for (const c of candidates) {
    if (Array.isArray(c) && looksLikeUserArray(c)) return c;
  }
  const first = findFirstArray(resp?.data ?? resp);
  if (looksLikeUserArray(first)) return first;
  return [];
}
function normalizeUsers(arr) {
  return (Array.isArray(arr) ? arr : [])
    .map((u) => {
      let email = u?.email;
      if (!email) {
        const k = Object.keys(u || {}).find((key) => /email/i.test(key));
        if (k) email = u[k];
      }
      if (!email) return null;

      // Find an id across possible shapes
      const rawId =
        u?.user_id ??
        u?.id ??
        u?.userId ??
        u?.UserId ??
        u?.profile?.user_id ??
        u?.profile?.id;
      const user_id = rawId != null && rawId !== "" ? String(rawId) : null;
      if (!user_id) return null;

      const username = u?.username ?? u?.first_name ?? u?.last_name ?? "";
      return { user_id, email, username };
    })
    .filter(Boolean)
    .sort((a, b) => a.email.localeCompare(b.email));
}

/* ---------------- component ---------------- */
const ShareModal = ({
  open,
  onClose,
  onShare, // if provided, called with (userIds, selectedUsers)
  title = "Share with people",
  defaultSelectedEmails = [],
  query_name = "",
  saved_query_id = "",
  ownerEmail, // optional: pass owner from parent
  ownerUserId, // optional: pass owner from parent
  excludeUserIds = [], // optional: user_ids to exclude (e.g. existing channel members)
}) => {
  const { getUsers } = useUserApi();
  const { createChannel } = useCollaboration();

  const [loading, setLoading] = React.useState(false);
  const [allUsers, setAllUsers] = React.useState([]); // owner-excluded, id-valid
  const [selected, setSelected] = React.useState([]); // array of user objects
  const [query, setQuery] = React.useState("");
  const [sharing, setSharing] = React.useState(false);

  // ----- derive owner from props OR storage (login) -----
  const stored = React.useMemo(() => {
    const a = StorageHelper.get("userDetails");
    const b = StorageHelper.get("userInfo");
    return a?.user_id ? a : b;
  }, []);
  const ownerEmailNorm = norm(ownerEmail ?? stored?.email);
  const ownerIdStr = toId(ownerUserId ?? stored?.user_id);

  const isOwnerUser = React.useCallback(
    (u) =>
      (ownerEmailNorm ? norm(u?.email) === ownerEmailNorm : false) ||
      (ownerIdStr ? toId(u?.user_id) === ownerIdStr : false),
    [ownerEmailNorm, ownerIdStr]
  );

  const getUsersRef = React.useRef(getUsers);
  React.useEffect(() => {
    getUsersRef.current = getUsers;
  }, [getUsers]);

  // Stable key for exclude list so effect doesn't re-run on every render when
  // used from Saved Queries (no exclude list = new [] each time otherwise)
  const excludeUserIdsKey = React.useMemo(
    () =>
      (Array.isArray(excludeUserIds) ? excludeUserIds : [])
        .map((id) => (id != null ? String(id).trim() : ""))
        .filter(Boolean)
        .sort()
        .join(","),
    [excludeUserIds]
  );

  /* fetch candidates when opened */
  React.useEffect(() => {
    if (!open) return;

    let active = true;
    setLoading(true);
    setAllUsers([]);
    setSelected([]);
    setQuery("");

    const excludeArr = (excludeUserIdsKey || "").split(",").filter(Boolean);
    const excludeIdSet = new Set(excludeArr);

    (async () => {
      try {
        const resp = await getUsersRef.current();
        if (!active) return;

        const raw = extractUsersArrayFlexible(resp);
        const normalized = normalizeUsers(raw);

        // Exclude owner + existing members + invalid ids (double guard)
        const filtered = normalized.filter(
          (u) =>
            !isOwnerUser(u) &&
            isValidUser(u) &&
            !excludeIdSet.has(toId(u.user_id))
        );

        setAllUsers(filtered);

        // Seed selected from defaults (minus owner)
        if (
          Array.isArray(defaultSelectedEmails) &&
          defaultSelectedEmails.length
        ) {
          const want = new Set(
            defaultSelectedEmails
              .filter(Boolean)
              .map(norm)
              .filter((e) => e && e !== ownerEmailNorm)
          );
          setSelected(filtered.filter((u) => want.has(norm(u.email))));
        }
      } catch (e) {
        if (active) {
          console.error("getUsers failed:", e);
          setAllUsers([]);
          setSelected([]);
        }
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [open, defaultSelectedEmails, isOwnerUser, ownerEmailNorm, excludeUserIdsKey]);

  /* ensure owner can never be in selection if props/storage change */
  React.useEffect(() => {
    setSelected((prev) =>
      prev.filter((u) => !isOwnerUser(u) && isValidUser(u))
    );
  }, [isOwnerUser]);

  /* search filter for display only */
  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allUsers;
    return allUsers.filter(
      (u) =>
        norm(u.email).includes(q) ||
        (u.username && u.username.toLowerCase().includes(q))
    );
  }, [allUsers, query]);

  /* ---- SELECT ALL: operate on the FULL valid list (original behavior) ---- */
  const validAll = React.useMemo(
    () => allUsers.filter(isValidUser),
    [allUsers]
  );
  const allSelected =
    validAll.length > 0 && selected.length === validAll.length;
  const someSelected = selected.length > 0 && selected.length < validAll.length;

  const toggleAll = () => {
    setSelected(allSelected ? [] : validAll);
  };

  /* toggle one row by user_id */
  const toggleOne = (user) => {
    if (!isValidUser(user) || isOwnerUser(user)) return;
    setSelected((prev) => {
      const exists = prev.some((p) => p.user_id === user.user_id);
      return exists
        ? prev.filter((p) => p.user_id !== user.user_id)
        : [...prev, user];
    });
  };

  const removeChip = (email) => {
    setSelected((prev) => prev.filter((p) => p.email !== email));
  };

  /* share */
  const handleShare = async () => {
    if (sharing) return;
    let userIds = selected
      .map((u) => u.user_id)
      .filter((id) => id != null && String(id).trim() !== "");
    if (onShare) {
      onClose?.();
      onShare(userIds, selected);
      return;
    }
    setSharing(true);
    try {
      const channelCreated = await createChannel(
        query_name,
        userIds,
        String(saved_query_id ?? "")
      );
      if (channelCreated?.success) {
        onClose?.();
      } else {
        console.error("Failed to create channel:", channelCreated?.error);
      }
    } finally {
      setSharing(false);
    }
  };

  if (!open) return null;

  return (
    <ShareOverlay onClick={onClose}>
      <ShareContainer onClick={(e) => e.stopPropagation()}>
        <ShareContent>
          {/* Header */}
          <ShareHeader>
            <ShareTitle>{title}</ShareTitle>
            <CloseButton aria-label="Close" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </CloseButton>
          </ShareHeader>

          {/* Selected chips */}
          {selected.length > 0 ? (
            <ChipsWrap hasChips>
              {selected.map((u) => (
                <PillChip
                  key={u.user_id}
                  label={u.email}
                  onDelete={() =>
                    setSelected((prev) =>
                      prev.filter((p) => p.user_id !== u.user_id)
                    )
                  }
                  variant="outlined"
                  color="primary"
                />
              ))}
            </ChipsWrap>
          ) : (
            <EmptyChipsHint>No recipients selected.</EmptyChipsHint>
          )}

          {/* Search */}
          <SearchInput
            placeholder="Search by email"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
            size="small"
            fullWidth
          />

          {/* List */}
          <ListContainer>
            <SelectAllRow onClick={toggleAll}>
              <Checkbox
                edge="start"
                checked={allSelected}
                indeterminate={someSelected}
              />
              <Typography sx={{ fontWeight: 700 }}>Select All</Typography>
            </SelectAllRow>

            {loading ? (
              <LoadingWrap>
                <CircularProgress size={18} />
                <Typography variant="caption" sx={{ ml: 1 }}>
                  Loading users…
                </Typography>
              </LoadingWrap>
            ) : filtered.length === 0 ? (
              <EmptyWrap>No matches.</EmptyWrap>
            ) : (
              filtered.map((u) => {
                const disabled = !isValidUser(u) || isOwnerUser(u);
                const isChecked = selected.some((s) => s.user_id === u.user_id);
                return (
                  <UserRow
                    key={u.user_id || u.email}
                    onClick={() => !disabled && toggleOne(u)}
                    role="button"
                    aria-disabled={disabled ? "true" : "false"}
                  >
                    <Checkbox
                      edge="start"
                      checked={isChecked}
                      disabled={disabled}
                    />
                    <MailOutlineIcon fontSize="small" />
                    <div className="text">
                      <Typography className="email">{u.email}</Typography>
                      {u.username ? (
                        <Typography className="name" variant="caption">
                          {u.username}
                        </Typography>
                      ) : null}
                    </div>
                  </UserRow>
                );
              })
            )}
          </ListContainer>

          {/* Footer */}
          <Footer>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {selected.length} selected{allSelected ? " (all)" : ""}
            </Typography>
            <div className="actions">
              <ClearButton onClick={() => setSelected([])}>Clear</ClearButton>
              <ShareButton
                onClick={handleShare}
                disabled={selected.length === 0 || sharing}
              >
                {sharing ? (
                  <CircularProgress size={16} sx={{ color: "white" }} />
                ) : (
                  "Share"
                )}
              </ShareButton>
            </div>
          </Footer>
        </ShareContent>
      </ShareContainer>
    </ShareOverlay>
  );
};

export default ShareModal;
